import React from "react"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router-dom"
import {
  getBackFallbackPath,
  getDefaultDashboardPath,
} from "utils/routeUtils"

const BackButton = ({ fallbackTo, showLabel = false, className = "" }) => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()

  const isRtl =
    i18n.language === "sa" ||
    document.documentElement.getAttribute("dir") === "rtl"

  const goBack = event => {
    event?.preventDefault?.()
    event?.stopPropagation?.()

    if (fallbackTo) {
      navigate(fallbackTo)
      return
    }

    const fallback =
      getBackFallbackPath(location.pathname) ||
      getDefaultDashboardPath(location.pathname)

    const historyIdx = window.history.state?.idx
    const canUseHistoryBack =
      (typeof historyIdx === "number" && historyIdx > 0) ||
      window.history.length > 1

    if (canUseHistoryBack) {
      navigate(-1)
      return
    }

    navigate(fallback)
  }

  const buttonClassName = [
    "btn",
    showLabel
      ? "btn-light btn-sm d-inline-flex align-items-center"
      : "p-0 border-0 bg-transparent d-inline-flex align-items-center justify-content-center back-button-icon",
    className,
  ]
    .filter(Boolean)
    .join(" ")

  return (
    <button
      type="button"
      className={buttonClassName}
      onClick={goBack}
      aria-label={t("Back")}
    >
      <i
        className={`bx bx-arrow-back fs-5${showLabel ? " me-1" : ""}`}
        style={isRtl ? { transform: "scaleX(-1)" } : undefined}
        aria-hidden="true"
      />
      {showLabel ? t("Back") : null}
    </button>
  )
}

BackButton.propTypes = {
  /** Explicit route when history cannot go back (overrides path inference). */
  fallbackTo: PropTypes.string,
  /** Show translated "Back" label next to the icon. */
  showLabel: PropTypes.bool,
  className: PropTypes.string,
}

export default BackButton
